import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PERMISSION_NAME, PermissionService, UtilsService, toSafeImageBlobUrl } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { Subject, catchError, debounceTime, map, throwError } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../../utils/src/lib/services/api-url.service';
import { ShapeLayerModel, ShapeLayerStyleListModel, ShapeStyleModel } from '../../../models/shape.model';
import { NormalizePipe } from '../../../pipe/normalize.pipe';
import { UuidPipe } from '../../../pipe/uuid.pipe';
import { MapService } from '../../../services/map.service';
import { ReportDialogComponent } from '../../report-dialog/report-dialog.component';
import { DraggableLegendComponent } from '../ebw-layers-configurator/draggable-legend/draggable-legend.component';
import { LayerStyleFormComponent } from './add-layer-style-form/add-layer-style-form.component';
import { ShapeLayerFormComponent } from './add-shape-layer-form/add-shape-layer-form.component';
import { ShowLayerStylesComponent } from './show-layer-styles/show-layer-styles.component';

@Component({
  selector: 'ebw-monorepo-shape-configurator',
  templateUrl: './shape-layers-configurator.component.html',
  styleUrls: ['./shape-layers-configurator.component.scss'],
  providers: [UuidPipe, NormalizePipe]
})
export class ShapeLayersConfiguratorComponent implements OnInit, OnDestroy {
  public layerStylesOptions: ShapeLayerStyleListModel[];
  public layersList: {
    id: string;
    label: string;
    visible: boolean;
    owner: string;
    accessibility: any[];
    info: ShapeLayerModel;
  }[] = [];
  public canAddLayer = false;
  public canDeleteLayer = false;
  public canEditLayer = false;
  public canAddStyle = false;
  public publicPermission: boolean;

  private _keyToIgnore = ['id', 'geometry', 'deleted', 'user_id', 'context_id', 'tenant_id', 'layer_id'];

  destroy$ = new Subject<void>();

  constructor(
    private _translateService: TranslateService,
    private _ebwModalService: EbwModalService,
    private _dialog: MatDialog,
    private _mapService: MapService,
    private _httpClient: HttpClient,
    private _sanitizer: DomSanitizer,
    private _uuidPipe: UuidPipe,
    private _normalizePipe: NormalizePipe,
    private _i18nService: I18NService,
    private _utilsService: UtilsService,
    private _permissionService: PermissionService,
    private _apiUrlService: ApiUrlService
  ) {}

  public ngOnInit(): void {
    this.publicPermission = this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.VISIBILITY_FILE_LAYER);

    this.canAddLayer =
      this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.ADD_SHAPE_LAYER) &&
      (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_CSV) ||
        this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_SHP) ||
        this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_KML) ||
        this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_KMZ));

    this.canEditLayer =
      this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.EDIT_SHAPE_LAYER) &&
      (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_CSV) ||
        this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_SHP) ||
        this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_KML) ||
        this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.FILE_LAYER_KMZ));

    this.canDeleteLayer = this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.DELETE_SHAPE_LAYER);

    this.canAddStyle = this._permissionService.checkPermission(PERMISSION_NAME.MAP.SHAPE.ADD_LAYER_STYLE);

    this._getLayerStylesOptions();

    this._mapService.mapConfigurationLoaded$.pipe(debounceTime(500)).subscribe({
      next: (res) => {
        this._getLayersList();
      }
    });
  }

  private _getLayersList() {
    let tempList: { id: string; label: string; visible: boolean; owner: string; accessibility: any[] }[] = this._mapService.getCustomLayers('SHAPE');

    this.layersList = [];
    if (tempList && tempList.length > 0) {
      this._getInfoLayers(
        tempList.map((element) => {
          return element.id;
        })
      ).subscribe({
        next: (res: any) => {
          this.layersList = [];
          if (res.length > 0) {
            tempList.forEach((layer) => {
              const layerInfo = res.find((info) => {
                return info.layer_id === layer.id;
              });

              if (layerInfo) {
                this.layersList.push({ ...layer, info: layerInfo });
              }
            });
          } else {
            if (tempList.length > 0) {
              this._ebwModalService.openErrorModal({
                title: this._translateService.instant('common.error'),
                content: this._translateService.instant('common.data_retrieving_error')
              });
            }
          }
        },
        error: (err) => {
          this.layersList = [];
          this._ebwModalService.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: this._translateService.instant('common.data_retrieving_error')
          });
        }
      });
    }
  }

  public setVisibility(layer: { id: string; label: string; visible: boolean }) {
    this._mapService.setLayerVisibility(layer.id, layer.visible);
  }

  public addNewShapeLayer() {
    const ref = this._dialog.open(ShapeLayerFormComponent, {
      width: '50%',
      data: {
        layerStylesOptions: this.layerStylesOptions
      }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp === 'RELOAD') {
        this._mapService.emitReloadAllConfRequest();
      }
    });
  }

  public addNewLayerStyle(styleToEdit: ShapeStyleModel = undefined) {
    let obj = {
      layerStylesOptions: this.layerStylesOptions
    };
    if (typeof styleToEdit != 'undefined') {
      obj['shapeStyle'] = styleToEdit;
    }
    const ref = this._dialog.open(LayerStyleFormComponent, {
      width: '50%',
      data: obj
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp === 'RELOAD') {
        this._mapService.emitReloadConfRequest();
        this._getLayerStylesOptions();
      }
    });
  }

  public removeLayer(layer: { id: string; label: string; visible: boolean; owner: string; accessibility: any[]; info: ShapeLayerModel }) {
    const message = this._translateService.instant('map.sidebar.shape.delete_modal_message', layer.label);

    this._ebwModalService
      .openConfirmModal({
        content: message,
        title: this._translateService.instant('map.sidebar.shape.delete_modal_title')
      })
      .afterClosed()
      .subscribe((result) => {
        if (result == true) {
          this._removeLayer(layer);
        }
      });
  }

  public updateLayer(layer: { id: string; label: string; visible: boolean; owner: string; accessibility: any[]; info: ShapeLayerModel }) {
    const ref = this._dialog.open(ShapeLayerFormComponent, {
      width: '50%',
      data: {
        layer,
        layerStylesOptions: this.layerStylesOptions
      }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp === 'RELOAD') {
        this._mapService.removeLayer(layer.id);
        this._mapService.emitReloadConfRequest();
        // this._subscription = this._mapService.mapConfigurationLoaded$.subscribe((value) => {
        //   this._getLayersList();
        // });
        this._mapService.clearCache();
      }
    });
  }

  public infoLayer(layer: { id: string; label: string; visible: boolean; owner: string; accessibility: any[]; info: ShapeLayerModel }) {
    const formattedInfo: any = safeJsonClone(<any>layer.info);

    const isPrivate = layer.accessibility.some((rel: any) => rel.user_id != null || rel.group_id != null);
    Object.assign(formattedInfo, { accessibility: isPrivate ? 'private' : 'public' });

    formattedInfo.label = this._uuidPipe.transform(formattedInfo.label);

    this._ebwModalService.openCustomModal(
      {
        content: '',
        title: `Dettaglio ${this._normalizePipe.transform(this._uuidPipe.transform(formattedInfo.label))}`
      },
      ReportDialogComponent,
      {
        data: {
          styleContainer: 'custom-report-dialog-container',
          styleWrapperItem: 'custom-wrapper-item',
          styleWrapperData: 'custom-wrapper-data',
          styleKeyValue: 'custom-item-key-value',
          styleKey: 'custom-item-key',
          styleValue: 'custom-item-value',
          data: Object.keys(formattedInfo)
            .filter((key) => !this._keyToIgnore.includes(key) && formattedInfo[key] != null && key != 'legend')
            .map((key) => ({
              key: this._translateService.instant(`map.sidebar.shape.info.${key}`),
              value: formattedInfo[key]
            })),
          showStructure: false
        }
      }
    );
  }

  private _getLayerStylesOptions() {
    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    this._httpClient
      .get(this._apiUrlService.createUrl(API_URLS.MAP.LAYER.STYLES), opts)
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res: any) => {
          res.styles.style.forEach((el) => {
            const imageUrl = this._apiUrlService.createUrl(API_URLS.MAP.LAYER.GET_LEGEND);
            el['image$'] = this._httpClient
              .get(`${imageUrl}?style=${el.image}`, { ...opts, responseType: 'blob' })
              .pipe(map((val) => toSafeImageBlobUrl(this._sanitizer, val)));
          });

          this.layerStylesOptions = res.styles.style;
        },
        error: (err) => {
          this._ebwModalService.openErrorModal({
            content: err?.error?.message,
            title: this._translateService.instant('common.error')
          });
        }
      });
  }

  private _removeLayer(layer: { id: string; label: string; visible: boolean; owner: string; accessibility: any[]; info: ShapeLayerModel }) {
    let isOwner = layer.owner === sessionStorage.getItem(LoginService.USER_ID_KEY);

    if (!isOwner) {
      this._ebwModalService.openErrorModal({
        content: 'Non si può eliminare un livello di cui non si è owner',
        title: this._translateService.instant('common.error')
      });
      return;
    }
    let params = new HttpParams();

    params = params.set('id', layer.id);
    const auth = 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY);
    const user = sessionStorage.getItem(LoginService.USER_ID_KEY);
    const opts = {
      headers: new HttpHeaders({ Authorization: auth, user }),
      params
    };

    this._httpClient
      .delete(this._apiUrlService.createUrl(API_URLS.MAP.LAYER.DELETE_LAYER), opts)
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res: any) => {
          this._ebwModalService.openSuccessModal({
            title: this._translateService.instant('map.sidebar.shape.delete_success'),
            content: ''
          });

          this._mapService.removeLayer(layer.id);
          this._mapService.emitReloadConfRequest();
        },
        error: (_) => {
          this._ebwModalService.openErrorModal({
            content: this._translateService.instant('common.data_retrieving_error'),
            title: this._translateService.instant('common.error')
          });
        }
      });
  }

  private _getInfoLayers(layers: string[]) {
    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    return this._httpClient.post(this._apiUrlService.createUrl(API_URLS.MAP.LAYER.GET_INFO), { layers }, opts).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public normalizeLabel(label: string) {
    return label.replace(/\_/g, ' ');
  }

  openDialog(shapeLayer: ShapeLayerModel): void {
    let formattedInfo: any = safeJsonClone(<any>shapeLayer);

    formattedInfo.label = this._uuidPipe.transform(formattedInfo.label);

    const imageUrl = this._apiUrlService.createUrl(API_URLS.MAP.LAYER.GET_LEGEND);

    const opts = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY),
        user: sessionStorage.getItem(LoginService.USER_ID_KEY)
      })
    };

    this._httpClient
      .get(`${imageUrl}?style=${formattedInfo.style}`, { ...opts, responseType: 'blob' })
      .pipe(map((val) => toSafeImageBlobUrl(this._sanitizer, val)))
      .subscribe((res) => {
        this._dialog.open(DraggableLegendComponent, {
          hasBackdrop: false,
          data: { title: formattedInfo.label, image: res }
        });
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getCustomStylesList() {
    const ref = this._dialog.open(ShowLayerStylesComponent, {
      width: '50%',
      data: {
        layerStylesOptions: this.layerStylesOptions
      }
    });

    ref.afterClosed().subscribe((resp) => {
      if (typeof resp == 'string') {
        this._utilsService.getRequest(this._apiUrlService.createUrl(API_URLS.MAP.LAYER.GET_STYLE) + '?title=' + resp, {}).subscribe({
          next: (value) => {
            this.addNewLayerStyle(value);
          },
          error: (error) => {
            this._ebwModalService.openErrorModal({
              title: this._i18nService.getInstantValue('common.error'),
              content: `${this._i18nService.getInstantValue('common.data_retrieving_error')} \n ${error.message}`
            });
          }
        });
      }
      if (resp == -1) {
        this.addNewLayerStyle();
      }
    });
  }

  public centerMap(shapeLayer: ShapeLayerModel): void {
    let formattedInfo: any = safeJsonClone(<any>shapeLayer);
    let [minX, minY, maxX, maxY] = formattedInfo.bbox.split(',').map((el: string) => Number(el));
    let center = [minX + (maxX - minX) / 2, minY + (maxY - minY) / 2];
    this._mapService.centerMapOnCoordinatesAndExtent(center, [minX, minY, maxX, maxY]);
  }
}
